import { formatDate } from '@/lib/date-time';
/**
 * @file routes/_app/risk-assessment/$caseId.tsx
 * @description 風險評估詳情頁。
 *
 * 功能：
 * - 顯示案件現有風險評估歷史
 * - 提供新建風險評估表單（riskScore 1-10 + 風險因素勾選清單 + 建議）
 *
 * 視覺：PageShell + SectionCard + DataField
 *
 * 角色：
 * - admin/manager/auditor：可建立評估
 * - decider：唯讀
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Progress,
  Row,
  Tag,
  Timeline,
} from 'antd';
import { ArrowLeft, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { PageShell, SectionCard, StatusPill } from '@/components/shell';
import { color, space } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/risk-assessment/$caseId')({
  component: RiskAssessmentDetailPage,
});

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

interface RiskAssessment {
  id: number;
  caseId: number;
  riskScore: number;
  factors: RiskFactor[] | null;
  recommendation: string | null;
  notes: string | null;
  assessedAt: string;
}

interface RiskFactor {
  id: string;
  label: string;
  checked: boolean;
}

interface CaseBasic {
  id: number;
  caseCode: string;
  clientName: string | null;
  workflowState: string;
}

// ─── 預設風險因素清單 ─────────────────────────────────────────────────────────

const DEFAULT_FACTORS: Omit<RiskFactor, 'checked'>[] = [
  { id: 'new_applicant', label: '首次申請（新申請人）' },
  { id: 'previous_nc', label: '前次稽核有重大 NC' },
  { id: 'complex_operation', label: '作業複雜（多地點/多產品）' },
  { id: 'subcontractor', label: '涉及外包/加工廠' },
  { id: 'high_risk_products', label: '高風險產品類別' },
  { id: 'complaint_history', label: '近期收到客訴' },
  { id: 'staff_turnover', label: '關鍵人員異動' },
  { id: 'supply_chain_change', label: '供應鏈重大變更' },
];

// ─── 風險等級 ─────────────────────────────────────────────────────────────────

function getRiskLevel(score: number): {
  label: string;
  tone: 'danger' | 'warning' | 'success';
  strokeColor: string;
} {
  if (score >= 8) return { label: '高風險', tone: 'danger', strokeColor: '#ff4d4f' };
  if (score >= 5) return { label: '中風險', tone: 'warning', strokeColor: '#fa8c16' };
  return { label: '低風險', tone: 'success', strokeColor: '#52c41a' };
}

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * 風險評估詳情頁面。
 */
function RiskAssessmentDetailPage() {
  const { caseId } = Route.useParams();
  const caseIdNum = Number(caseId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user!);
  const isDecider = user.roles.includes('decider');

  const [form] = Form.useForm();
  const [checkedFactors, setCheckedFactors] = useState<string[]>([]);

  // ─── 查詢案件基本資訊 ─────────────────────────────────────────────
  const { data: caseData } = useQuery<{ item: CaseBasic }>({
    queryKey: ['case-basic', caseIdNum],
    queryFn: () => apiClient.get(`/api/cases/${caseIdNum}`),
  });

  // ─── 查詢風險評估歷史 ─────────────────────────────────────────────
  const { data: assessmentsData, isLoading } = useQuery<{ items: RiskAssessment[] }>({
    queryKey: ['risk-assessments-by-case', caseIdNum],
    queryFn: () => apiClient.get(`/api/cases/${caseIdNum}/risk-assessments`),
  });

  const caseBasic = caseData?.item;
  const assessments = assessmentsData?.items ?? [];
  const latestAssessment = assessments[0];

  // ─── 新建風險評估 ────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (body: unknown) => apiClient.post(`/api/cases/${caseIdNum}/risk-assessment`, body),
    onSuccess: () => {
      message.success('風險評估已提交');
      form.resetFields();
      setCheckedFactors([]);
      void qc.invalidateQueries({ queryKey: ['risk-assessments-by-case', caseIdNum] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const handleSubmit = (values: { riskScore: number; recommendation?: string; notes?: string }) => {
    const factors = DEFAULT_FACTORS.map((f) => ({
      ...f,
      checked: checkedFactors.includes(f.id),
    }));
    createMutation.mutate({ ...values, factors });
  };

  return (
    <PageShell
      title={`風險評估 — ${caseBasic?.caseCode ?? `案件 #${caseId}`}`}
      meta={
        caseBasic && (
          <StatusPill tone="info" size="sm">
            {caseBasic.workflowState}
          </StatusPill>
        )
      }
      actions={
        <Button
          icon={<ArrowLeft size={14} />}
          onClick={() => void navigate({ to: '/risk-assessment' })}
        >
          返回列表
        </Button>
      }
    >
      <Row gutter={[16, 16]}>
        {/* 左：新建評估表單 */}
        <Col xs={24} lg={12}>
          <SectionCard icon={<ShieldAlert size={14} />} title="新建風險評估">
            {isDecider ? (
              <Alert type="info" message="驗證決定者為唯讀角色，無法新建評估" showIcon />
            ) : (
              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                  name="riskScore"
                  label="風險評分（1-10）"
                  rules={[
                    { required: true, message: '請輸入風險評分' },
                    {
                      validator: (_, v) =>
                        v >= 1 && v <= 10
                          ? Promise.resolve()
                          : Promise.reject(new Error('評分需介於 1 到 10')),
                    },
                  ]}
                >
                  <InputNumber min={1} max={10} style={{ width: '100%' }} />
                </Form.Item>

                {/* 風險因素勾選 */}
                <Form.Item label="風險因素">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: space[2] }}>
                    {DEFAULT_FACTORS.map((f) => (
                      <div key={f.id}>
                        <Checkbox
                          checked={checkedFactors.includes(f.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCheckedFactors((prev) => [...prev, f.id]);
                            } else {
                              setCheckedFactors((prev) => prev.filter((id) => id !== f.id));
                            }
                          }}
                        >
                          {f.label}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                </Form.Item>

                <Form.Item name="recommendation" label="建議事項">
                  <Input.TextArea rows={3} placeholder="根據評分建議的稽核強度/頻率..." />
                </Form.Item>
                <Form.Item name="notes" label="備註">
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={createMutation.isPending} block>
                  提交評估
                </Button>
              </Form>
            )}
          </SectionCard>
        </Col>

        {/* 右：評估歷史 */}
        <Col xs={24} lg={12}>
          {/* 最新評估摘要 */}
          {latestAssessment && (
            <div style={{ marginBottom: space[4] }}>
              <SectionCard title="最新評估摘要">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: space[4],
                    marginBottom: space[3],
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 36,
                        fontWeight: 700,
                        color: color.text.primary,
                        lineHeight: 1,
                      }}
                    >
                      {latestAssessment.riskScore}
                    </div>
                    <div style={{ fontSize: 12, color: color.text.muted }}>/ 10</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    {(() => {
                      const level = getRiskLevel(latestAssessment.riskScore);
                      return (
                        <>
                          <Progress
                            percent={latestAssessment.riskScore * 10}
                            strokeColor={level.strokeColor}
                            showInfo={false}
                          />
                          <div style={{ marginTop: space[2] }}>
                            <StatusPill tone={level.tone} size="sm">
                              {level.label}
                            </StatusPill>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
                {latestAssessment.recommendation && (
                  <div style={{ fontSize: 14, color: color.text.secondary }}>
                    <span style={{ color: color.text.tertiary }}>建議：</span>
                    {latestAssessment.recommendation}
                  </div>
                )}
              </SectionCard>
            </div>
          )}

          {/* 歷史記錄 */}
          <SectionCard title="評估歷史">
            {isLoading ? (
              <div>載入中...</div>
            ) : assessments.length === 0 ? (
              <Alert type="info" message="尚無評估記錄" showIcon />
            ) : (
              <Timeline
                items={assessments.map((a) => {
                  const level = getRiskLevel(a.riskScore);
                  return {
                    color:
                      level.tone === 'danger'
                        ? 'red'
                        : level.tone === 'warning'
                          ? 'orange'
                          : 'green',
                    children: (
                      <div key={a.id}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: space[2] }}>
                          <StatusPill tone={level.tone} size="sm">
                            評分 {a.riskScore}
                          </StatusPill>
                          <span style={{ fontSize: 12, color: color.text.muted }}>
                            {formatDate(a.assessedAt)}
                          </span>
                        </div>
                        {a.recommendation && (
                          <div style={{ fontSize: 14, marginTop: space[2] }}>
                            {a.recommendation}
                          </div>
                        )}
                        {/* 勾選的風險因素 */}
                        {Array.isArray(a.factors) && a.factors.some((f) => f.checked) && (
                          <div style={{ marginTop: space[2] }}>
                            {a.factors
                              .filter((f) => f.checked)
                              .map((f) => (
                                <Tag
                                  key={f.id}
                                  color="orange"
                                  style={{ marginBottom: 2, fontSize: 11 }}
                                >
                                  {f.label}
                                </Tag>
                              ))}
                          </div>
                        )}
                      </div>
                    ),
                  };
                })}
              />
            )}
          </SectionCard>
        </Col>
      </Row>
    </PageShell>
  );
}
